import { Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { map, mergeMap, catchError, switchMap, startWith, skipWhile } from "rxjs/operators";
import { Observable, forkJoin, throwError, Subscription, interval, takeWhile, EMPTY, lastValueFrom, take, of } from "rxjs";
import * as _ from 'lodash';
import { FileService, ToastrService, CacheService, ITypeRelevantAspectBO, IRelevantAspectBO } from '@mapfre-tech/amap3';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';

@Injectable({
  providedIn: 'root'
})

export class RelevantAspectService {

  className = 'RelevantAspectService'

  constructor(private http: HttpClient, private fileService: FileService,
    private toastService: ToastrService, private translateService: TranslateService, private cacheService: CacheService) { }

  /*
  * Relevant Aspects
  */
  getRelevantAspects(): Observable<IRelevantAspectBO[]> {
    return this.http.get<IRelevantAspectBO[]>(environment.base_URL + "/relevant-aspects");
  }

  getRelevantAspectById(id: number): Observable<IRelevantAspectBO> {
    return this.http.get<IRelevantAspectBO>(environment.base_URL + `/relevant-aspects/${id}`);
  }

  newRelevantAspect(assessment: IRelevantAspectBO): Observable<IRelevantAspectBO[]> {
    return this.http.post<IRelevantAspectBO[]>(environment.base_URL + "/relevant-aspects", assessment);
  }

  newRelevantAspectWithFiles(assessment: IRelevantAspectBO): Observable<IRelevantAspectBO[]> {
    // PRE FILES INI
    const filesToUpload = ((assessment.documents ?? []) as unknown[]).filter((f): f is File => f instanceof File);
    // PRE FILES END
    //CHECK IF THERE ARE NEW FILES
    if (!_.isEmpty(filesToUpload)) {
      return this.newRelevantAspect(assessment).pipe(mergeMap((res: IRelevantAspectBO[]) => {
        const entityId = String(res?.[0]?.idRelevantAspectPk ?? '');
        const observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'relevantAspect', entityId));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));
      }))
    }
    else {
      return this.newRelevantAspect(assessment)
    }
  }

  updateRelevantAspect(assessment: IRelevantAspectBO, id: number): Observable<IRelevantAspectBO[]> {
    return this.http.put<IRelevantAspectBO[]>(environment.base_URL + `/relevant-aspects/${id}`, assessment)
  }

  updateRelevantAspectWithFiles(assessment: IRelevantAspectBO, id: number): Observable<IRelevantAspectBO[]> {
    // PRE FILES INI
    const filesToUpload = ((assessment.documents ?? []) as unknown[]).filter((f): f is File => f instanceof File);
    // PRE FILES END
    //CHECK IF THERE ARE NEW FILES
    if (!_.isEmpty(filesToUpload)) {
      return this.updateRelevantAspect(assessment, id).pipe(mergeMap((res: IRelevantAspectBO[]) => {
        const entityId = String(res?.[0]?.idRelevantAspectPk ?? id);
        const observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'relevantAspect', entityId));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));
      }))
    }
    else {
      return this.updateRelevantAspect(assessment, id);
    }
  }

  deleteRelevantAspect(id: number): Observable<IRelevantAspectBO> {
    return this.http.delete<IRelevantAspectBO>(environment.base_URL + `/relevant-aspects/${id}`);
  }

  /*
   * Relevant Aspect Types
   */
  getRelevantAspectTypes(cache = true, cacheKey = "relevantAspectTypes"): Observable<ITypeRelevantAspectBO[]> {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey) as object as Observable<ITypeRelevantAspectBO[]>;
    } else {
      const observable = this.http.get<ITypeRelevantAspectBO[]>(environment.base_URL + "/relevant-aspects-types").pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }


  /**
   * Comments type
   */

  getCommentsType() {
    return [{ title: "COMENTARIOS_NOVEDADES_LEGALES", form: "txtLegalNews" },
    { title: "COMENTARIOS_COLABORACIONES", form: "txtColab" },
    { title: "COMENTARIOS_AUDITORIA_EXTERNA", form: "txtAuditExt" },
    { title: "COMENTARIOS_REGULADORES", form: "txtRegulators" },
    { title: "COMENTARIOS_FRAUDE", form: "txtFrauds" },
    { title: "COMENTARIOS_FORMACION", form: "txtFormation" },
    { title: "COMENTARIOS_OTROS", form: "txtOthers" }];
  }



  exportRelevantAspects(key: string, params: any, data = null) {

    const user_locale = window.navigator.language;
    _.set(params, "locale", user_locale);

    _.set(params, "key", key + '/data.json');

    this.toastService.add({ severity: "info", summary: this.translateService.instant("TOAST.GENERAL.INFO_LOADING_TITLE"), detail: this.translateService.instant("TOAST.GENERAL.INFO_EXPORT"), life: 240000 });

    // Call aws gateway to generate pre signed url
    return this.http.get<any[]>(environment.base_URL + "/exported-url", { params })
      .pipe(mergeMap((res1: any) => {

        // Remove key paramenter and add url parameter
        _.unset(params, "key")
        _.set(params, "url", res1)

        // Call Mapgre Gateway to generate excel with exoprted jobs
        return this.http.get<any[]>(environment.base_URL + "/exported-aspects", { params })
          .pipe(mergeMap((res2: any) => {

            // Remove url paramenter and add key parameter
            const paramsExcel = {}
            _.set(paramsExcel, "keydata", key + '/data.json');
            _.set(paramsExcel, "keyexcel", key + '/export.xlsx');

            const method = 'get';
            const url = '/create-excel';

            return this.http[method]<any[]>(environment.base_URL + url, { params: paramsExcel })
              .pipe(mergeMap((res3: any) => {

                // Remove url paramenter and add key parameter
                const paramsDownload = {}
                _.set(paramsDownload, "onlyDownload", true)

                const body = {
                  key: key + '/export.xlsx'
                }

                // Download excel
                return this.http.post(environment.base_URL + `/download`, body, { params: paramsDownload })
                  .pipe(mergeMap((res4: any) => {

                    // Download file
                    const options = {
                      responseType: "blob" as 'json'
                    }

                    // Download file form S3
                    return this.http.get(res4, options);

                  }), map(res5 => {

                    //Clear toast notification
                    this.toastService.clear()
                    //Download generated Excel
                    FileSaver.saveAs(res5, `export.xlsx`);

                    // Delete file when downloaded
                    this.delete(key + '/export.xlsx').subscribe(() => { });
                    return res5;

                  }), catchError(err => {
                    this.toastService.add({
                      severity: "error",
                      summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                      detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
                    });
                    return throwError(err);
                  }))


              }))
          }))

      }), map(res6 => {
        return res6;
      }), catchError<Observable<any>, any>((err) => {

        if(err.status != 500){ 

          const target = {};
          _.set(target, "keyFile", key + '/data.json');

          let ob: Observable<any>;
          let cont = 0;

          //Condiciones de timeout y repetición de request a check-files
          function checkConditions(response: any){
            if(cont <18 && response.statusCode === 404){
                cont++;
                return true;
            }else{
                cont++;
                return false
            }
          }

          //Multiple request to lambda chech-files. Stops when file has been found or timeout
          ob = interval(5000)
          .pipe(
            startWith(0),
            switchMap(() => this.http.get<any>(environment.base_URL + "/check-files", { params: target })),
            takeWhile(response => checkConditions(response), true)
          )

          forkJoin([ob]).subscribe(([check]) => {

            if(check.statusCode === 200){

                const paramsExcel = {}
                _.set(paramsExcel, "keydata", key + '/data.json');
                _.set(paramsExcel, "keyexcel", key + '/export.xlsx');

                const method = 'get';
                const url = '/create-excel';

                const result = forkJoin( [this.http[method]<any[]>(environment.base_URL + url, { params: paramsExcel })])

                return forkJoin([result]).subscribe(([resul1]) => {

                  // Remove url paramenter and add key parameter
                  const paramsDownload = {}
                  _.set(paramsDownload, "onlyDownload", true)

                  const body = {
                      key: key + '/export.xlsx'
                  }

                  // Download excel
                  const result2 = this.http.post(environment.base_URL + `/download`, body, { params: paramsDownload })
                      .pipe(mergeMap((res: any) => {
                        // Download file
                        const options = {
                            responseType: "blob" as 'json'
                        }

                        //Clear toast notification
                        this.toastService.clear()

                        // Download file form S3
                        return this.http.get(res, options);

                      }), map(res => {

                        // Delete file when downloaded
                        this.delete(key).subscribe(() => { });
                        return res;

                      }), catchError(err => {
                        this.toastService.add({
                            severity: "error",
                            summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                            detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
                        });
                        return throwError(err);
                      }))

                      return forkJoin([result2]).subscribe(([resul2]) => {
                        //Download generated Excel
                        FileSaver.saveAs(resul2, `export.xlsx`);
                        return resul2;
                      });
                });
            }else{
                //Clear toast info notification
                this.toastService.clear()

                this.toastService.add({
                  severity: "error",
                  summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                  detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TIMEOUT"), life: 5000
                });
            }
        });
        }else{
          //Clear toast info notification
          setTimeout(() => {
            this.toastService.clear()
          },4000)
        }

      return EMPTY;
     }))
  }

  delete(key: string) {
    const params = {
      key
    }
    return this.http.post(environment.base_URL + "/delete", { params }).pipe(catchError(err => {
      this.toastService.add({
        severity: "error",
        summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
        detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
      });
      return throwError(err);
    }));
  }

  addRelevantAspectsType(params: any): Observable<ITypeRelevantAspectBO> {
    return this.http.post<ITypeRelevantAspectBO>(environment.base_URL + "/relevant-aspects-types", params);
  }

  updateRelevantAspectsType(params: any): Observable<ITypeRelevantAspectBO> {
    return this.http.put<ITypeRelevantAspectBO>(environment.base_URL + "/relevant-aspects-types", params);
  }

}

